import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    Boxes, CalendarClock, ClipboardList, Info, Loader2, PlusCircle, Save,
} from "lucide-react";

import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { workOrdersService } from "@/services/work_orders.service";
import { customersService } from "@/services/customers.service";
import { techniciansService } from "@/services/technicians.service";
import { assetsService } from "@/services/assets.service";
import { getErrorMessage } from "@/services/api";
import { OS_CATEGORIES, OS_PRIORITIES } from "@/config/constants";
import { formatDate, formatDateTime } from "@/lib/utils";
import { NewEquipmentDialog } from "@/features/assets/NewEquipmentDialog";

const schema = z.object({
    title: z.string().min(3, "Informe o título"),
    description: z.string().optional().nullable(),
    customer_id: z.string().min(1, "Selecione o cliente"),
    asset_id: z.string().min(1, "Selecione o equipamento"),
    technician_ids: z.array(z.string()).default([]),
    priority: z.string(),
    category: z.string(),
});

function Field({ label, error, children, className = "", required = false }) {
    return (
        <div className={`space-y-1.5 ${className}`}>
            <Label className="text-sm font-medium">
                {label} {required && <span className="text-danger">*</span>}
            </Label>
            {children}
            {error && <div className="text-xs text-danger">{error}</div>}
        </div>
    );
}

function EquipmentInfoCard({ assetId, stats }) {
    const { data: asset, isLoading } = useQuery({
        queryKey: ["asset", assetId],
        queryFn: () => assetsService.get(assetId),
        enabled: !!assetId,
    });
    if (!assetId) return null;
    if (isLoading || !asset) {
        return <Skeleton className="h-32 w-full" />;
    }
    return (
        <div
            className="grid gap-4 rounded-xl border bg-muted/30 p-4 md:grid-cols-4"
            data-testid="wo-equipment-info"
        >
            <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Equipamento
                </div>
                <div className="mt-0.5 flex items-center gap-2">
                    <Boxes className="h-4 w-4 text-muted-foreground" />
                    <span className="font-mono text-sm font-medium">{asset.internal_code}</span>
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">{asset.category}</div>
            </div>
            <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Fabricante · Modelo
                </div>
                <div className="mt-0.5 text-sm">{asset.manufacturer || "—"} {asset.model ? `· ${asset.model}` : ""}</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">Série: {asset.serial_number || "—"}</div>
            </div>
            <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Situação
                </div>
                <div className="mt-0.5">
                    {asset.operational_status === "parado" ? (
                        <Badge variant="outline" className="border-danger/40 text-danger">Parado</Badge>
                    ) : (
                        <Badge variant="outline" className="border-success/40 text-success">Operante</Badge>
                    )}
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                    Patrimônio: {asset.customer_asset_tag || "—"}
                </div>
            </div>
            <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Histórico
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 text-sm">
                    <ClipboardList className="h-3.5 w-3.5 text-muted-foreground" />
                    {stats?.data?.total_wos ?? 0} OS anteriores
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <CalendarClock className="h-3 w-3" />
                    Última intervenção:{" "}
                    {stats?.data?.last_finalized_at
                        ? formatDate(stats.data.last_finalized_at)
                        : stats?.data?.last_wo_at
                            ? formatDateTime(stats.data.last_wo_at)
                            : "nenhuma"}
                </div>
            </div>
        </div>
    );
}

export default function WorkOrderFormPage() {
    const { id } = useParams();
    const isEdit = !!id;
    const navigate = useNavigate();
    const qc = useQueryClient();

    const detail = useQuery({
        queryKey: ["work_order", id],
        queryFn: () => workOrdersService.get(id),
        enabled: isEdit,
    });
    const customers = useQuery({
        queryKey: ["customers-select"],
        queryFn: () => customersService.list({ pageSize: 100 }),
    });
    const technicians = useQuery({
        queryKey: ["technicians-select"],
        queryFn: () => techniciansService.list({ pageSize: 100 }),
    });

    const { register, handleSubmit, reset, watch, setValue, control, formState: { errors } } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            title: "", description: "", customer_id: "", asset_id: "",
            technician_ids: [], priority: "media", category: "Corretiva",
        },
    });

    const [newEquipmentOpen, setNewEquipmentOpen] = useState(false);

    const customerId = watch("customer_id");
    const assetId = watch("asset_id");
    const techIds = watch("technician_ids") || [];

    const assets = useQuery({
        queryKey: ["assets-by-customer", customerId],
        queryFn: () => assetsService.list({ customer_id: customerId, pageSize: 100 }),
        enabled: !!customerId,
    });

    const stats = useQuery({
        queryKey: ["asset-stats", assetId],
        queryFn: () => assetsService.getStats(assetId),
        enabled: !!assetId,
    });

    useEffect(() => {
        if (isEdit && detail.data) {
            const d = detail.data;
            reset({
                title: d.title || "",
                description: d.description || "",
                customer_id: d.customer_id || "",
                asset_id: d.asset_id || (d.asset_ids && d.asset_ids[0]) || "",
                technician_ids: d.technician_ids || [],
                priority: d.priority || "media",
                category: d.category || "Corretiva",
            });
        }
    }, [isEdit, detail.data, reset]);

    // Handler when the inline "Novo equipamento" dialog creates a new asset:
    // 1) seed the cache with the new item for instant UI feedback
    // 2) trigger a refetch to sync with the server
    // 3) select the new asset in the form
    function handleNewEquipmentCreated(created) {
        if (!created?.id) return;
        // Seed the cache so the <Select> renders the new option immediately.
        qc.setQueryData(["assets-by-customer", customerId], (old) => {
            if (!old) {
                return { items: [created], total: 1, page: 1, page_size: 100, total_pages: 1 };
            }
            const items = old.items || [];
            if (items.some((it) => it.id === created.id)) return old;
            return {
                ...old,
                items: [created, ...items],
                total: (old.total || 0) + 1,
            };
        });
        // Defer the setValue to the NEXT paint cycle so Radix Select has time
        // to receive & mount the new SelectItem before we point value at it.
        // (Radix Select silently keeps the placeholder when value has no
        // matching Item yet.)
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                setValue("asset_id", created.id, { shouldDirty: true, shouldValidate: true });
            });
        });
        // Background sync with server
        qc.invalidateQueries({ queryKey: ["assets-by-customer", customerId] });
    }

    // Reset asset_id ONLY when the customer changes (not on every list update).
    // Using a ref avoids false resets when the query cache is optimistically
    // updated (e.g. after creating a new equipment inline).
    const prevCustomerRef = React.useRef(customerId);
    useEffect(() => {
        if (prevCustomerRef.current !== customerId) {
            prevCustomerRef.current = customerId;
            // Only clear when it's a *change*, not the initial mount.
            if (customerId) setValue("asset_id", "");
        }
    }, [customerId, setValue]);

    const save = useMutation({
        mutationFn: (data) => {
            const payload = { ...data };
            Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
            return isEdit
                ? workOrdersService.update(id, payload)
                : workOrdersService.create(payload);
        },
        onSuccess: (res) => {
            toast.success(isEdit ? "OS atualizada." : "OS criada.");
            qc.invalidateQueries({ queryKey: ["work_orders"] });
            qc.invalidateQueries({ queryKey: ["dashboard"] });
            qc.invalidateQueries({ queryKey: ["asset-timeline"] });
            navigate(`/work-orders/${res.id || id}`);
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao salvar OS.")),
    });

    function toggleTech(x) {
        setValue(
            "technician_ids",
            techIds.includes(x) ? techIds.filter((v) => v !== x) : [...techIds, x],
            { shouldDirty: true },
        );
    }

    function openNewEquipmentDialog() {
        if (!customerId) {
            toast.error("Selecione um cliente antes de criar um equipamento.");
            return;
        }
        setNewEquipmentOpen(true);
    }

    return (
        <form onSubmit={handleSubmit(save.mutate)} className="space-y-6">
            <PageHeader
                title={isEdit ? "Editar OS" : "Nova OS"}
                description={isEdit ? "Ajustar dados de uma OS existente." : "Nova solicitação de serviço vinculada a um equipamento."}
                backTo={isEdit ? `/work-orders/${id}` : "/work-orders"}
                breadcrumb={[
                    { label: "Início", to: "/dashboard" },
                    { label: "OS", to: "/work-orders" },
                    { label: isEdit ? "Editar" : "Nova" },
                ]}
                actions={
                    <>
                        <Button type="button" variant="ghost" onClick={() => navigate("/work-orders")}>
                            Cancelar
                        </Button>
                        <Button type="submit"
                            className="bg-secondary text-secondary-foreground hover:opacity-90"
                            disabled={save.isPending}
                            data-testid="wo-save-button">
                            {save.isPending
                                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                : <Save className="mr-2 h-4 w-4" />}
                            Salvar
                        </Button>
                    </>
                }
            />

            <Card className="rounded-xl border shadow-sm">
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold">Solicitação</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                    <Field label="Título" required error={errors.title?.message} className="md:col-span-2">
                        <Input {...register("title")} data-testid="wo-title" placeholder="Ex.: Motor com barulho" />
                    </Field>
                    <Field label="Cliente" required error={errors.customer_id?.message}>
                        <Select value={watch("customer_id") || ""}
                            onValueChange={(v) => { setValue("customer_id", v); setValue("asset_id", ""); }}>
                            <SelectTrigger data-testid="wo-customer"><SelectValue placeholder="Selecionar cliente" /></SelectTrigger>
                            <SelectContent>
                                {(customers.data?.items || []).map((c) => (
                                    <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Categoria">
                        <Select value={watch("category")} onValueChange={(v) => setValue("category", v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {OS_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Prioridade">
                        <Select value={watch("priority")} onValueChange={(v) => setValue("priority", v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {OS_PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Descrição da solicitação" className="md:col-span-2">
                        <Textarea rows={4} {...register("description")}
                            data-testid="wo-description"
                            placeholder="Descreva o que o cliente reportou…" />
                    </Field>
                </CardContent>
            </Card>

            {/* Equipamento (obrigatório) */}
            <Card className="rounded-xl border shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div>
                        <CardTitle className="text-sm font-semibold">Equipamento</CardTitle>
                        <p className="text-[11px] text-muted-foreground">Selecione o equipamento do cliente que será atendido.</p>
                    </div>
                    <Button
                        type="button" variant="outline" size="sm"
                        onClick={openNewEquipmentDialog}
                        disabled={!customerId}
                        data-testid="wo-new-equipment-button"
                    >
                        <PlusCircle className="mr-2 h-4 w-4" /> Novo equipamento
                    </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                    {!customerId ? (
                        <div className="flex items-center gap-2 rounded-lg border border-dashed bg-muted/30 px-4 py-6 text-xs text-muted-foreground">
                            <Info className="h-4 w-4" />
                            Selecione um cliente para escolher o equipamento.
                        </div>
                    ) : (
                        <>
                            <Field label="Equipamento" required error={errors.asset_id?.message}>
                                <Controller
                                    control={control}
                                    name="asset_id"
                                    render={({ field }) => (
                                        <Select
                                            value={field.value || ""}
                                            onValueChange={field.onChange}
                                        >
                                            <SelectTrigger data-testid="wo-asset-select">
                                                <SelectValue placeholder="Selecionar equipamento" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {assets.isLoading && <SelectItem value="__loading__" disabled>Carregando…</SelectItem>}
                                                {!assets.isLoading && (assets.data?.items || []).length === 0 && (
                                                    <SelectItem value="__empty__" disabled>Nenhum equipamento cadastrado para este cliente</SelectItem>
                                                )}
                                                {(assets.data?.items || []).map((a) => (
                                                    <SelectItem key={a.id} value={a.id} data-testid={`wo-asset-option-${a.internal_code}`}>
                                                        <span className="font-mono">{a.internal_code}</span>
                                                        <span className="text-muted-foreground">
                                                            {" · "}{a.category}{a.manufacturer ? ` · ${a.manufacturer}` : ""}
                                                        </span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </Field>
                            {assetId && <EquipmentInfoCard assetId={assetId} stats={stats} />}
                        </>
                    )}
                </CardContent>
            </Card>

            <Card className="rounded-xl border shadow-sm">
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold">Técnico designado ({techIds.length})</CardTitle>
                </CardHeader>
                <CardContent>
                    {(technicians.data?.items || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground">Sem técnicos cadastrados.</div>
                    ) : (
                        <div className="max-h-56 space-y-1 overflow-y-auto p-360-thin-scrollbar">
                            {technicians.data.items.map((t) => {
                                const sel = techIds.includes(t.id);
                                return (
                                    <button key={t.id} type="button" onClick={() => toggleTech(t.id)}
                                        className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm ${sel ? "bg-accent" : "hover:bg-accent/40"}`}
                                        data-testid={`wo-tech-${t.id}`}>
                                        <span>{t.name} <span className="text-xs text-muted-foreground">· {t.engineering_specialty || "—"}</span></span>
                                        {sel && <Badge variant="outline" className="border-secondary/40 text-secondary">✓</Badge>}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Modal inline: criar equipamento sem sair da tela (PRD Módulo 02) */}
            <NewEquipmentDialog
                open={newEquipmentOpen}
                onOpenChange={setNewEquipmentOpen}
                customerId={customerId}
                customerName={
                    (customers.data?.items || []).find((c) => c.id === customerId)?.legal_name
                }
                onCreated={handleNewEquipmentCreated}
            />
        </form>
    );
}
